import SHA1 from 'crypto-js/sha1'
import auth from 'src/services/auth.service.js'
import cookies from 'src/services/cookies.service.js'
import userHelpers from 'src/services/user.service.js'
import config from 'src/app.config.js'
import router from 'src/router'
import { checkTokenExpiration } from 'asokoinsight-tokens/src/helpers'

import { SET_CURRENT_USER, CLEAR_CURRENT_USER } from './mutation-types'

const state = {
  currentUser: {},
}

const mutations = {
  [SET_CURRENT_USER](state, user) {
    state.currentUser = user
  },
  [CLEAR_CURRENT_USER](state) {
    state.currentUser = {}
  },
}

const sessionHandler = ({ commit, dispatch }, data, vm) => {
  const displayName = data.displayName || data.username
  const userCohort = userHelpers.getUserCohort(data)
  const publicRoles = data.roles
  const email = data.primaryEmail

  const user = {
    name: displayName,
    email,
    accessToken: data.token.accessToken,
    userId: data.id,
    isActiveSubscriber: false,
    cohort: userCohort,
    roles: publicRoles,
    firstName: data.data.firstName,
    lastName: data.data.lastName,
    org: data.data.organisation,
  }

  commit(SET_CURRENT_USER, user)
  dispatch('tokens/setTokensData', data.token, { root: true })

  const { sector, organisation, newsletter } = data.data

  return auth.isActiveSubscriber(user.userId).then(response => {
    const data = response.data
    const isActiveSubscriber = !!data.isAllAccess
    user.isActiveSubscriber = isActiveSubscriber
    user.cohort = data.accessLevel || userCohort
    user.haveBundles = data.haveBundles
    user.onlinePayment = data.onlinePayment

    commit(SET_CURRENT_USER, user)

    vm.$ga.event({
      eventCategory: 'Authentication',
      eventAction: 'User Login',
      eventLabel: 'UserID ' + user.userId,
    })
    vm.$gtag.event('User Login', {
      'event_category': 'Authentication',
      'event_label': 'UserID ' + user.userId,
    })
  })
}

const actions = {
  'user/removeLegacyCookie'() {
    const cookie = cookies.getCookie('userInfo')
    if (cookie) {
      cookies.removeCookie('userInfo')
    }
  },
  setCurrentUser({ commit }, data) {
    commit(SET_CURRENT_USER, data)
  },
  clearCurrentUser({ commit, dispatch }) {
    dispatch('tokens/clearTokens', null, { root: true })
    commit(CLEAR_CURRENT_USER)
  },
  checkAndRefresh({ dispatch, getters }) {
    const refreshExpiresAt = localStorage.getItem('refreshExpiresAt')
    let isExpired = false
    if (refreshExpiresAt) {
      isExpired = checkTokenExpiration(refreshExpiresAt)
    }
    if (refreshExpiresAt && !isExpired) {
      dispatch('tokens/refreshAccessToken', null, { root: true })
        .catch(err => {
          this._vm.$log(err)
          if (!getters.isChecked) {
            dispatch('finishUserCheck', true)
          }
          if (router.currentRoute.meta.sidebar) {
            dispatch('showModal', 'login')
          }
        })
        .finally(() => {
          dispatch('tokens/setTokenChecked', null, { root: true })
        })
    } else {
      dispatch('tokens/setTokenChecked', null, { root: true })
    }
  },
  login({ commit, dispatch }, payload) {
    return auth.login(payload).then(res => {
      const data = res.data
      return sessionHandler({ commit, dispatch }, data, this._vm)
    })
  },
  'user/handleSessionUpdate'({ commit, dispatch }, data) {
    return sessionHandler({ commit, dispatch }, data, this._vm)
  },
  'user/handleRefreshFail'({ dispatch }, err) {
    this._vm.$log(err)
    dispatch('clearCurrentUser')
  },
  logout({ commit, dispatch }) {
    return auth
      .logout()
      .then(res => {
        commit(CLEAR_CURRENT_USER)
        dispatch('tokens/clearTokens', null, { root: true })
        this._vm.$ga.event({
          eventCategory: 'Authentication',
          eventAction: 'User Logout',
        })
        this._vm.$gtag.event('User Logout', {
          'event_category': 'Authentication',
          'event_label': 'User Logout',
        })
      })
      .catch(err => {
        if (_.get(err, 'response.status') === 401) {
          location.reload()
        }
        this._vm.$log(err)
      })
  },
  createAccount({ commit, dispatch }, userData) {
    const newAccount = { ...userData }
    if (userData.userType) {
      newAccount.userType = userData.userType
    }
    const emailHash = SHA1(userData.email).toString()
    newAccount.username = userData.firstName + '_' + emailHash

    const analyticsUtm = _.pickBy(
      userData,
      (val, key) => _.startsWith(key, 'utm') && val
    )
    if (!_.isEmpty(analyticsUtm)) {
      newAccount.analyticsUtm = analyticsUtm
    }

    return auth.createAccount(newAccount).then(res => {
      const user = res.data.user
      const displayName = user.displayName || user.username
      const userCohort = userHelpers.getUserCohort(user)
      const publicRoles = user.roles
      const currentUser = {
        name: displayName,
        email: user.primaryEmail,
        accessToken: res.data.token.accessToken,
        userId: user._id,
        isActiveSubscriber: false,
        cohort: userCohort,
        roles: publicRoles,
        firstName: user.data.firstName,
        lastName: user.data.lastName,
      }

      commit(SET_CURRENT_USER, currentUser)
      dispatch('tokens/setTokensData', res.data.token, { root: true })

      this._vm.$ga.event({
        eventCategory: 'Authentication',
        eventAction: 'User Registration',
        eventLabel: 'UserID ' + user._id,
      })
      this._vm.$ga.event({
        eventCategory: 'Authentication',
        eventAction: 'User Login',
        eventLabel: 'UserID ' + user._id,
      })
      this._vm.$gtag.event('User Registration', {
        'event_category': 'Authentication',
        'event_label': 'UserID ' + user._id,
      })
      this._vm.$gtag.event('User Login', {
        'event_category': 'Authentication',
        'event_label': 'UserID ' + user._id,
      })

      if (userData.newsletter) {
        this._vm.$ga.event({
          eventCategory: 'Subscription',
          eventAction: 'News Brief Subscription',
          eventLabel: 'UserID ' + user._id,
        })
        this._vm.$gtag.event('News Brief Subscription', {
          'event_category': 'Subscription',
          'event_label': 'UserID ' + user._id,
        })
      }
    })
  },
}

const getters = {
  currentUser: state => state.currentUser,
  isLoggedIn: state => !_.isEmpty(state.currentUser),
  userToken: state => state.currentUser.accessToken,
  userCohort: state => state.currentUser.cohort || 'anonymous',
  userId: state => state.currentUser.userId || 'guest',
  isSubscriber: state =>
    state.currentUser.cohort === 'basic' ||
    state.currentUser.cohort === 'advanced' ||
    state.currentUser.haveBundles,
  isHavingBundles: state => state.currentUser.haveBundles,
  isSharingAccessRole: state =>
    _.intersection(state.currentUser.roles, ['admin', 'project-admin']).length >
    0,
  isInternal: state =>
    _.intersection(state.currentUser.roles, ['admin', 'points-admin']).length >
    0,
}

export default {
  state,
  mutations,
  actions,
  getters,
}
